import React, { useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Select from 'components/Select'
import Input from 'components/Input'
import { SiteContext } from 'providers/site'

const filterOptions = (options, level1Selected, level2Selected = null) => {
  return {
    level1Habitats: options.level1Habitats,
    level2Habitats: options.level2Habitats.filter((element) => {
      return element.Level1HabitatId === level1Selected
    }),
    level3Habitats: options.level3Habitats.filter((element) => {
      return element.Level1HabitatId === level1Selected && element.Level2HabitatId === level2Selected
    })
  }
}

const defaultOptionsLength = 1
const defaultHabitatLevel3Length = 0
const HabitatsContent = ({
  item,
  options,
  createHabitats,
  updateHabitats,
  defaultHabitatsOptions,
  defaultPercentage,
  disabled
}) => {
  const { siteProposalFieldValidation } = useContext(SiteContext)
  const initialLevelOneHabitat = defaultHabitatsOptions.level1Habitats.find((element) => {
    return element.value === item.Level1HabitatId
  })
  const initialLevelTwoHabitat = defaultHabitatsOptions.level2Habitats.find((element) => {
    return element.value === item.Level2HabitatId
  })
  const initialLevelThreeHabitat = defaultHabitatsOptions.level3Habitats.find((element) => {
    return element.value === item.Level3HabitatId
  })

  const [selectLevelOneHabitats, setSelectLevelOneHabitats] = useState(initialLevelOneHabitat || null)
  const [selectLevelTwoHabitats, setSelectLevelTwoHabitats] = useState(initialLevelTwoHabitat || null)
  const [selectLevelThreeHabitats, setSelectLevelThreeHabitats] = useState(initialLevelThreeHabitat || null)
  const [localPercentage, setLocalPercentage] = useState(item.SiteHabitatPercentage || defaultPercentage)

  const isNumber = (val) => {
    return typeof val === 'number' && Number.isFinite(val)
  }

  const [availableOptions, setAvailableOptions] = useState(
    filterOptions(options, item.Level1HabitatId, item.Level2HabitatId)
  )

  const handleChangeLevelOneHabitats = (e) => {
    setAvailableOptions(filterOptions(options, e.value))
    setSelectLevelOneHabitats(e)
    setSelectLevelTwoHabitats(null)
    setSelectLevelThreeHabitats(null)

    if (availableOptions.level2Habitats.length === 0) {
      createHabitats({
        Level1HabitatId: e.value
      })
    }
  }

  const handleChangeLevelTwoHabitats = (e) => {
    const result = filterOptions(options, selectLevelOneHabitats.value, e.value)
    setAvailableOptions(result)
    setSelectLevelTwoHabitats(e)
    setSelectLevelThreeHabitats(null) // Reset level three selection

    if (item.id === '' && result.level3Habitats.length === defaultHabitatLevel3Length) {
      createHabitats({ Level2HabitatId: e.value })
    } else {
      updateHabitats(item.id, { Level2HabitatId: e.value })
    }
  }

  const handleChangeLevelThreeHabitats = (e) => {
    setSelectLevelThreeHabitats(e)
    if (item.id === '') {
      createHabitats({ Level3HabitatId: e.value })
    } else {
      updateHabitats(item.id, { Level3HabitatId: e.value })
    }
  }

  const handlePercentageChange = (e) => {
    const inputValue = e.target.value
    setLocalPercentage(inputValue)
    const inputPercentage = inputValue === '' ? null : Number.parseFloat(inputValue)
    if (isNumber(inputPercentage) && inputPercentage >= 0 && inputPercentage <= 100) {
      updateHabitats(item.id, { SiteHabitatPercentage: inputPercentage })
    } else {
      console.error('Invalid habitat percentage')
    }
  }

  useEffect(() => {
    setAvailableOptions(filterOptions(options, item.Level1HabitatId, item.Level2HabitatId))
    setSelectLevelOneHabitats(initialLevelOneHabitat || null)
    setSelectLevelTwoHabitats(initialLevelTwoHabitat || null)
    setSelectLevelThreeHabitats(initialLevelThreeHabitat || null)
  }, [item, options])

  return (
    <Container width="100%" padding="0px">
      <Row>
        <Col md={6}>
          <Select
            value={selectLevelOneHabitats}
            marginTop="16px"
            onChange={handleChangeLevelOneHabitats}
            placeholder="selectLevelOneHabitat"
            options={availableOptions.level1Habitats}
            label="levelOne"
            infoTooltip="levelOneHabitatsHint"
            disabled={disabled}
            error={
              siteProposalFieldValidation.details.level1Habitat
                ? { field: '', error: '' }
                : {
                  field: 'selectLevelOneHabitats',
                  error: 'pleaseCompleteThisField'
                }
            }
          />
        </Col>
        {(availableOptions.level2Habitats.length >= defaultOptionsLength || selectLevelTwoHabitats) && (
          <Col md={6}>
            <Select
              value={selectLevelTwoHabitats}
              marginTop="16px"
              onChange={handleChangeLevelTwoHabitats}
              placeholder="selectLevelTwoHabitat"
              options={availableOptions.level2Habitats}
              label="levelTwo"
              disabled={disabled}
              error={
                siteProposalFieldValidation.details.level2Habitat
                  ? { field: '', error: '' }
                  : {
                    field: 'selectLevelTwoHabitats',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        )}
      </Row>
      <Row>
        {(availableOptions.level3Habitats.length >= defaultOptionsLength || selectLevelThreeHabitats) && (
          <Col md={6}>
            <Select
              value={selectLevelThreeHabitats}
              marginTop="16px"
              onChange={handleChangeLevelThreeHabitats}
              placeholder="selectLevelThreeHabitat"
              options={availableOptions.level3Habitats}
              label="levelThree"
              disabled={disabled}
              error={
                siteProposalFieldValidation.details.level3Habitat
                  ? { field: '', error: '' }
                  : {
                    field: 'selectLevelThreeHabitats',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        )}
        <Col md={6}>
          <Input
            type="number"
            value={localPercentage}
            onChange={handlePercentageChange}
            label="habitatsPercentage"
            placeholder="habitatsPercentagePlaceholder"
            infoTooltip="habitatsPercentageHint"
            step="0.01"
            min="0"
            max="100"
            disabled={disabled}
            error={
              siteProposalFieldValidation.details.habitatPercentage
                ? { field: '', error: '' }
                : {
                  field: 'localPercentage',
                  error: 'pleaseCompleteThisField'
                }
            }
          />
        </Col>
      </Row>
    </Container>
  )
}

export default HabitatsContent

HabitatsContent.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    Level1HabitatId: PropTypes.number,
    Level2HabitatId: PropTypes.number,
    Level3HabitatId: PropTypes.number,
    SiteHabitatPercentage: PropTypes.number
  }).isRequired,
  options: PropTypes.shape({
    level1Habitats: PropTypes.array.isRequired,
    level2Habitats: PropTypes.array.isRequired,
    level3Habitats: PropTypes.array.isRequired
  }).isRequired,
  createHabitats: PropTypes.func.isRequired,
  updateHabitats: PropTypes.func.isRequired,
  defaultHabitatsOptions: PropTypes.shape({
    level1Habitats: PropTypes.array.isRequired,
    level2Habitats: PropTypes.array.isRequired,
    level3Habitats: PropTypes.array.isRequired
  }).isRequired,
  defaultPercentage: PropTypes.number,
  disabled: PropTypes.bool
}